import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Lock, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, local, errMsg } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { titleCase, fmtDateTime } from "@/lib/format";

// Developer control: activate the subscription paywall per organisation, at a time of their choosing.
export function SAPaywall() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");

  const { data: orgs = [], isLoading } = useQuery({ queryKey: ["sa-orgs"], queryFn: async () => (await api.get("/superadmin/organisations")).data || [] });
  const { data: cfgResp } = useQuery({ queryKey: ["paywall-configs"], queryFn: async () => (await local.get("/paywall/orgs")).data });

  const cfgMap = useMemo(() => {
    const m = {};
    (cfgResp?.configs || []).forEach((c) => (m[c.org_id] = c));
    return m;
  }, [cfgResp]);

  const setMut = useMutation({
    mutationFn: async (body) => (await local.post("/paywall/set", body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["paywall-configs"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const toggle = (org, enabled) => {
    const cfg = cfgMap[org.id];
    setMut.mutate({ org_id: org.id, enabled, plan_required: cfg?.plan_required || "standard" });
    toast.success(enabled ? `Paywall activated for ${org.name}` : `Paywall lifted for ${org.name}`);
  };
  const setPlan = (org, plan_required) => {
    const cfg = cfgMap[org.id];
    setMut.mutate({ org_id: org.id, enabled: cfg?.enabled ?? true, plan_required });
  };

  const filtered = orgs.filter((o) => !q || o.name.toLowerCase().includes(q.toLowerCase()));
  const activeCount = (cfgResp?.configs || []).filter((c) => c.enabled).length;

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Subscription Paywall</h1>
          <p className="mt-1 text-sm text-slate-400">Activate subscription enforcement per organisation, whenever you decide.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-400">{activeCount} active</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input data-testid="sa-paywall-search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="w-52 border-white/10 bg-white/5 pl-9 text-white placeholder:text-slate-500" />
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl border border-white/10 bg-teal-600/10 p-4 text-sm text-slate-300">
        <ShieldCheck className="mt-0.5 h-5 w-5 text-teal-400" />
        <p>The paywall stays <b>hidden</b> for every organisation until you switch it on here. When activated, that org’s clinical modules are blurred behind an upgrade wall until they move to the required paid plan.</p>
      </div>

      {isLoading ? <Loader full /> : (
        <div className="mt-6 space-y-3">
          {filtered.map((o) => {
            const cfg = cfgMap[o.id];
            const enabled = !!cfg?.enabled;
            return (
              <div key={o.id} data-testid={`sa-paywall-row-${o.id}`} className={`flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${enabled ? "border-amber-500/40 bg-amber-500/[0.06]" : "border-white/10 bg-white/[0.03]"}`}>
                <div className="flex items-center gap-3">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${enabled ? "bg-amber-500/20 text-amber-400" : "bg-white/5 text-slate-500"}`}><Lock className="h-5 w-5" /></span>
                  <div>
                    <p className="font-medium text-white">{o.name}</p>
                    <p className="text-xs text-slate-400">
                      Plan: <span className="uppercase">{o.plan || "trial"}</span>
                      {enabled && cfg?.activated_at && <span> · activated {fmtDateTime(cfg.activated_at)}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Requires</span>
                    <Select value={cfg?.plan_required || "standard"} onValueChange={(v) => setPlan(o, v)}>
                      <SelectTrigger data-testid={`sa-paywall-plan-${o.id}`} className="h-8 w-36 border-white/10 bg-slate-800 text-xs text-white"><SelectValue /></SelectTrigger>
                      <SelectContent>{["standard", "enterprise"].map((p) => <SelectItem key={p} value={p}>{titleCase(p)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold ${enabled ? "text-amber-400" : "text-slate-500"}`}>{enabled ? "ON" : "OFF"}</span>
                    <Switch data-testid={`sa-paywall-toggle-${o.id}`} checked={enabled} onCheckedChange={(v) => toggle(o, v)} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
